import axios from 'axios';
import { API_BASE_URL } from './api';
import {
  Task,
  TaskQueue,
  TaskFilterParams,
  TaskAnalytics,
  TaskComment,
  TaskType,
  TaskStatus,
  TaskPriority,
} from '../types/task.types';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CreateTaskDto {
  title: string;
  description?: string;
  type: TaskType;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDateTime?: string;
  queueId?: number;
  tags?: string[];
  clientIds?: number[];
  dealIds?: number[];
  assignedUserIds?: number[];
  associations?: Array<{
    recordType: string;
    recordId: number;
    isPrimary?: boolean;
  }>;
  reminders?: Array<{
    triggerAt: string;
    channel: string;
  }>;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  type?: TaskType;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDateTime?: string;
  queueId?: number;
  tags?: string[];
  outcome?: string;
  clientIds?: number[];
  dealIds?: number[];
}

export interface BulkTaskActionDto {
  taskIds: number[];
  status?: TaskStatus;
  assignedUserIds?: number[];
  queueId?: number;
  dueDateTime?: string;
}

export interface CreateTaskCommentDto {
  body: string;
  isInternal?: boolean;
}

export interface TaskListResponse {
  data: Task[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const taskService = {
  // Task CRUD
  getTasks: (filters?: TaskFilterParams): Promise<TaskListResponse> =>
    api.get('/tasks', { params: filters }).then((res) => res.data),

  getTask: (id: number): Promise<Task> =>
    api.get(`/tasks/${id}`).then((res) => res.data),

  createTask: (data: CreateTaskDto): Promise<Task> =>
    api.post('/tasks', data).then((res) => res.data),

  updateTask: (id: number, data: UpdateTaskDto): Promise<Task> =>
    api.patch(`/tasks/${id}`, data).then((res) => res.data),

  deleteTask: (id: number): Promise<void> =>
    api.delete(`/tasks/${id}`).then((res) => res.data),

  // Task actions
  completeTask: (id: number): Promise<Task> =>
    api.post(`/tasks/${id}/complete`).then((res) => res.data),

  reopenTask: (id: number): Promise<Task> =>
    api.post(`/tasks/${id}/reopen`).then((res) => res.data),

  bulkAction: (data: BulkTaskActionDto): Promise<{ message: string }> =>
    api.post('/tasks/bulk-action', data).then((res) => res.data),

  // User-specific views
  getMyTasks: (filters?: TaskFilterParams): Promise<TaskListResponse> =>
    api.get('/tasks/my-tasks', { params: filters }).then((res) => res.data),

  getOverdueTasks: (): Promise<TaskListResponse> =>
    api.get('/tasks/overdue').then((res) => res.data),

  getTaskAnalytics: (): Promise<TaskAnalytics> =>
    api.get('/tasks/analytics').then((res) => res.data),

  // Comments
  addComment: (taskId: number, data: CreateTaskCommentDto): Promise<TaskComment> =>
    api.post(`/tasks/${taskId}/comments`, data).then((res) => res.data),

  getComments: (taskId: number): Promise<TaskComment[]> =>
    api.get(`/tasks/${taskId}/comments`).then((res) => res.data),

  // Queues
  getQueues: (): Promise<TaskQueue[]> =>
    api.get('/task-queues').then((res) => res.data),

  getQueue: (id: number): Promise<TaskQueue> =>
    api.get(`/task-queues/${id}`).then((res) => res.data),

  getQueueTasks: (id: number): Promise<Task[]> =>
    api.get(`/task-queues/${id}/tasks`).then((res) => res.data),

  createQueue: (data: any): Promise<TaskQueue> =>
    api.post('/task-queues', data).then((res) => res.data),

  updateQueue: (id: number, data: any): Promise<TaskQueue> =>
    api.patch(`/task-queues/${id}`, data).then((res) => res.data),

  deleteQueue: (id: number): Promise<void> =>
    api.delete(`/task-queues/${id}`).then((res) => res.data),
};

export default taskService;

